import { useT } from "../../i18n/hooks.ts";
import { linkToAquifers, geometriesOfLiveEntities } from "../../model/gisLayer.ts";
import { assignOutletAquiferInBulk, type GisColumnMapping } from "../../model/projectEntities.ts";
import { TopologyAssignmentSection } from "../TopologyAssignmentSection.tsx";
import { AquifersForm, AquifersTable } from "../AquiferCatalog.tsx";
import type { DrawingTool } from "../drawingTools.ts";
import type { EvalHidProject } from "../../model/types.ts";
import { DrawButton } from "./DrawButton.tsx";
import { LinkGisPanel } from "./LinkGisPanel.tsx";
import { useProjectEditing } from "./hooks.ts";

function aquiferName(project: EvalHidProject, cod: number): string {
  return cod === 0 ? "—" : (project.aquifers.find((a) => a.aquiferCode === cod)?.name ?? `#${cod}`);
}

/**
 * "Aquifers" editor tab — same role as OutletPointsPanel.tsx but for
 * aquifers: draw, link its own GIS layer, catalog and which aquifer each
 * sub-basin drains into.
 */
export function AquifersPanel({
  onCreateAquifersFromGis,
  drawingMode,
  onStartDrawing,
  onCancelDrawing,
  selection,
  onChangeSelection,
}: {
  onCreateAquifersFromGis: (geojson: GeoJSON.FeatureCollection, mapping: GisColumnMapping) => void;
  drawingMode: DrawingTool | null;
  onStartDrawing: (tool: DrawingTool) => void;
  onCancelDrawing: () => void;
  /** Selection shared with the main map (see EditBar.tsx) — sub-basins are selected by clicking there, not on a separate small map. */
  selection?: Set<number>;
  onChangeSelection?: (selection: Set<number>) => void;
}) {
  const t = useT();
  const {
    project,
    onChangeProject: onChange,
    // SUB-BASINS layer (not its own) — for the assignment map of "which aquifer each sub-basin drains into".
    gisLayer,
    aquifersLayer,
    onChangeAquifersLayer,
    series,
    onChangeSeries,
  } = useProjectEditing();
  return (
    <div>
      <h3>{t("infoPopover.aquifers")}</h3>

      <AquifersForm project={project} onChange={onChange} />

      <DrawButton
        tool="aquifer"
        drawingMode={drawingMode}
        onStartDrawing={onStartDrawing}
        onCancelDrawing={onCancelDrawing}
      />

      <details className="collapsible-section" open={aquifersLayer.linked === 0}>
        <summary>{t("subBasinsPanel.linkGisLayer")}</summary>
        <LinkGisPanel
          entities={project.aquifers}
          layer={aquifersLayer}
          link={linkToAquifers}
          onLinked={onChangeAquifersLayer}
          existingGeometries={geometriesOfLiveEntities(aquifersLayer, project.aquifers)}
          onCreate={onCreateAquifersFromGis}
          singularName={t("drawing.aquifer")}
          pluralName={t("common.aquiferPlural")}
        />
      </details>

      <h4>{t("aquifersPanel.catalog")}</h4>
      <AquifersTable
        project={project}
        onChange={onChange}
        series={series}
        onChangeSeries={onChangeSeries}
      />

      {project.aquifers.length > 0 && (
        <TopologyAssignmentSection
          title={t("aquifersPanel.topologyTitle")}
          gisLayer={gisLayer}
          project={project}
          isAssigned={(c) => c.aquiferCode !== 0}
          valueLabel={(c) => aquiferName(project, c.aquiferCode)}
          options={project.aquifers.map((a) => ({
            value: a.aquiferCode,
            label: a.name,
          }))}
          buttonText={t("aquifersPanel.assignAquifer")}
          unassignValue={0}
          onAssign={(codes, value) => onChange(assignOutletAquiferInBulk(project, codes, value))}
          selection={selection}
          onChangeSelection={onChangeSelection}
        />
      )}
    </div>
  );
}
